import React, { useState } from 'react';
import '../styles/Services.css';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  detailedInfo: {
    overview: string;
    features: string[];
    pricing: string;
    duration: string;
  };
}

const services: Service[] = [
  {
    id: 'civil',
    title: 'Civil Law',
    description: 'Property disputes, contracts, and civil litigation matters',
    icon: 'fas fa-gavel',
    detailedInfo: {
      overview: 'Our civil law services cover a wide range of legal matters including property disputes, contract law, tort claims, and general civil litigation. We provide expert guidance for both individuals and businesses.',
      features: ['Property dispute resolution', 'Contract drafting and review', 'Civil litigation support', 'Legal documentation', 'Court representation'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  },
  {
    id: 'criminal',
    title: 'Criminal Law',
    description: 'Criminal defense and legal representation',
    icon: 'fas fa-shield-alt',
    detailedInfo: {
      overview: 'Expert criminal defense services providing comprehensive legal representation for various criminal matters. Our experienced lawyers ensure your rights are protected throughout the legal process.',
      features: ['Criminal defense representation', 'Bail applications', 'Court proceedings support', 'Legal advice and consultation', 'Case strategy development'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  },
  {
    id: 'matrimonial',
    title: 'Matrimonial',
    description: 'Marriage, divorce, and family law matters',
    icon: 'fas fa-heart',
    detailedInfo: {
      overview: 'Comprehensive matrimonial and family law services handling divorce, child custody, maintenance, and other family-related legal matters with sensitivity and expertise.',
      features: ['Divorce proceedings', 'Child custody arrangements', 'Maintenance and alimony', 'Marriage registration', 'Family dispute resolution'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  },
  {
    id: 'arbitration',
    title: 'Arbitration',
    description: 'Dispute resolution through arbitration',
    icon: 'fas fa-handshake',
    detailedInfo: {
      overview: 'Professional arbitration services offering efficient and cost-effective dispute resolution outside traditional court systems. Ideal for commercial and contractual disputes.',
      features: ['Commercial dispute arbitration', 'Contract dispute resolution', 'Arbitration proceedings management', 'Settlement negotiations', 'Award enforcement'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  },
  {
    id: 'mediation',
    title: 'Mediation',
    description: 'Peaceful conflict resolution services',
    icon: 'fas fa-users',
    detailedInfo: {
      overview: 'Expert mediation services facilitating peaceful resolution of disputes through structured dialogue and negotiation, helping parties reach mutually acceptable solutions.',
      features: ['Family mediation', 'Commercial mediation', 'Workplace dispute resolution', 'Community mediation', 'Settlement facilitation'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  },
  {
    id: 'negotiable',
    title: 'Negotiable Instruments',
    description: 'Cheque bounce and payment disputes',
    icon: 'fas fa-file-contract',
    detailedInfo: {
      overview: 'Specialized services for negotiable instrument matters including cheque bounce cases, promissory note disputes, and bill of exchange issues under the Negotiable Instruments Act.',
      features: ['Cheque bounce case handling', 'Recovery proceedings', 'Legal notice drafting', 'Court representation', 'Settlement negotiations'],
      pricing: 'Consultation available',
      duration: '60 minutes consultation'
    }
  }
];

interface ServicesProps {
  onOpenBooking: () => void;
}

const Services: React.FC<ServicesProps> = ({ onOpenBooking }) => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const handleBookService = (serviceId: string) => {
    console.log(`Booking service: ${serviceId}`);
    onOpenBooking();
  };

  const handleLearnMore = (service: Service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <h2 className="section-title animate-fadeInUp">Our Legal Services</h2>
        <p className="section-subtitle animate-fadeInUp">Comprehensive legal solutions for all your needs</p>
        <div className="services-grid">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className="service-card animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="service-icon">
                <i className={service.icon}></i>
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <div className="service-price" style={{ display: 'none' }}>₹2000</div>
              <div className="service-buttons">
                <button 
                  className="btn btn-outline" 
                  onClick={() => handleLearnMore(service)}
                >
                  Learn More
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleBookService(service.id)}
                >
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Service Info Modal */}
      {selectedService && (
        <div className="service-modal-overlay" onClick={closeModal}>
          <div className="service-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            <div className="service-modal-header">
              <div className="service-modal-icon">
                <i className={selectedService.icon}></i>
              </div>
              <h2>{selectedService.title}</h2>
            </div>
            <div className="service-modal-body">
              <div className="service-overview">
                <h3>Overview</h3>
                <p>{selectedService.detailedInfo.overview}</p>
              </div>
              <div className="service-features">
                <h3>What We Offer</h3>
                <ul>
                  {selectedService.detailedInfo.features.map((feature, index) => (
                    <li key={index}><i className="fas fa-check-circle"></i> {feature}</li>
                  ))}
                </ul>
              </div>
              <div className="service-pricing" style={{ display: 'none' }}>
                <div className="pricing-card">
                  <h3>Consultation Details</h3>
                  <div className="pricing-info">
                    <div className="price">{selectedService.detailedInfo.pricing}</div>
                    <div className="duration">{selectedService.detailedInfo.duration}</div>
                  </div>
                </div>
              </div>
              <div className="service-modal-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    closeModal();
                    handleBookService(selectedService.id);
                  }}
                >
                  Book Consultation Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;